/**
 * User role constants for SIME.
 * Maps tipoUsuarioId to role names.
 */
export const ROLES = {
  ADMINISTRADOR: 1,
  SUPERVISOR: 2,
  OPERADOR: 3,
  ENCARGADO: 4,
  COORDINADOR_CODELCO: 12,
  LIDER: 13,
} as const;

export type RoleId = (typeof ROLES)[keyof typeof ROLES];

export const ROLE_LABELS: Record<RoleId, string> = {
  [ROLES.ADMINISTRADOR]: 'Administrador',
  [ROLES.SUPERVISOR]: 'Supervisor',
  [ROLES.OPERADOR]: 'Operador',
  [ROLES.ENCARGADO]: 'Encargado',
  [ROLES.COORDINADOR_CODELCO]: 'Coordinador Codelco',
  [ROLES.LIDER]: 'Lider Cuadrilla',
};

// Single source of truth for "can mutate tickets/pautas (POST/PUT)".
// Used by both frontend (`canManageTickets`) and backend (`requireRole(...)`)
// to prevent drift across modules. DELETE intentionally keeps a narrower
// allowlist defined inline at each route.
export const TICKET_MANAGER_ROLES: readonly RoleId[] = [
  ROLES.ADMINISTRADOR,
  ROLES.ENCARGADO,
  ROLES.SUPERVISOR,
  ROLES.LIDER,
] as const;

export function canManageTickets(roleId: number | null | undefined): boolean {
  return roleId != null && (TICKET_MANAGER_ROLES as readonly number[]).includes(roleId);
}

// Single source of truth for "can manage planes y programas (POST/PUT/PATCH)".
// Deliberately narrower than TICKET_MANAGER_ROLES: planes are a planning
// function (strategic, periodic), so LIDER (Lider Cuadrilla, frontline) is
// excluded — they can VIEW their assignments and upload evidencia, but not
// create/edit plans. Mirrors Schwager MANAGERS=[ADMIN, PLANIFICADOR, SUPERVISOR, ITO].
// DELETE narrows further to ADMINISTRADOR inline at each route.
export const PLAN_MANAGER_ROLES: readonly RoleId[] = [
  ROLES.ADMINISTRADOR,
  ROLES.SUPERVISOR,
  ROLES.ENCARGADO,
] as const;

export function canManagePlanes(roleId: number | null | undefined): boolean {
  return roleId != null && (PLAN_MANAGER_ROLES as readonly number[]).includes(roleId);
}

// Roles a quienes un manager puede asignar actividades de plan (campo).
// Excluye managers (no se autoasignan). Lider+Operador+Encargado son
// los perfiles que ejecutan en terreno y suben evidencias.
export const PLAN_ASSIGNABLE_ROLES: readonly RoleId[] = [
  ROLES.OPERADOR,
  ROLES.LIDER,
  ROLES.ENCARGADO,
] as const;

// Single source of truth for "can be assigned to a cuadrilla (field crew)".
// Allowlist of the field roles only: SUPERVISOR (acts as crew lead),
// OPERADOR and LIDER. Deliberately EXCLUDES COORDINADOR_CODELCO (12) and
// ENCARGADO (4): both carry ámbitos for the closure / lote flow but are
// client-side or managerial roles, never field crew. ADMINISTRADOR and any
// unmapped tipo_usuario_id are excluded by omission. An allowlist (not a
// `!= COORDINADOR_CODELCO` blocklist) keeps the picker correct even when a
// non-crew user is mis-tagged — empirically only tipo {2,3,13} have ever
// appeared in cuadrilla_dotacion in prod.
export const CUADRILLA_ELIGIBLE_ROLES: readonly RoleId[] = [
  ROLES.SUPERVISOR,
  ROLES.OPERADOR,
  ROLES.LIDER,
] as const;

export function isCuadrillaEligible(roleId: number | null | undefined): boolean {
  return roleId != null && (CUADRILLA_ELIGIBLE_ROLES as readonly number[]).includes(roleId);
}
